import { useState } from 'react'
import { noop } from 'lodash-es'
import classnames from 'classnames'
import TextArea from '@components/forms/text_area'
import Link from '@components/Link'
import { markupAndSanitize } from '@utils/sanitize'

type TextAreaChangeEvent = {
  name?: string
  value: string
}
type Props = {
  id?: string
  name?: string
  value: string
  onChange: (changeObj: TextAreaChangeEvent) => void
  onBlur?: () => void
  writeClassName?: string
}

const RichMarkdownEditor = ({
  id,
  name,
  value = '',
  onChange = noop,
  onBlur = noop,
  writeClassName = 'textarea',
}: Props) => {
  const [mode, setMode] = useState('write') // write or preview
  const [showFormattingTip, setShowFormattingTip] = useState(false) // show or hide formatting tips

  const handlePreviewClick = () => {
    setMode('preview')
  }

  const handleWriteClick = () => {
    setMode('write')
  }

  const handleTipClick = () => {
    setShowFormattingTip(!showFormattingTip)
  }

  const handleChange = (obj: TextAreaChangeEvent) => {
    onChange(obj)
  }

  let content = null
  switch (mode) {
    case 'write':
      content = (
        <TextArea
          id={id}
          name={name}
          value={value}
          onChange={handleChange}
          onBlur={onBlur}
          className={writeClassName}
          autoSize={true}
        />
      )
      break

    case 'preview': {
      content = <div className="marked-up" dangerouslySetInnerHTML={{ __html: markupAndSanitize(value) }} />
      break
    }
  }
  const writeTabClassName = classnames('btn', 'rich-markdown-editor__tab', {
    'rich-markdown-editor__tab--selected': mode === 'write',
  })
  const previewTabClassName = classnames('btn', 'rich-markdown-editor__tab', {
    'rich-markdown-editor__tab--selected': mode === 'preview',
  })
  return (
    <div className="rich-markdown-editor">
      <div className="rich-markdown-editor__tabnav">
        <div className="rich-markdown-editor__tabs">
          <button type="button" className={writeTabClassName} onClick={handleWriteClick}>
            Write
          </button>
          <button type="button" className={previewTabClassName} onClick={handlePreviewClick}>
            Preview
          </button>
          <div className="rich-markdown-editor__tip-trigger-container">
            <button type="button" className="unbutton rich-markdown-editor__tip-trigger" onClick={handleTipClick}>
              Formatting help
            </button>
          </div>
        </div>
      </div>
      {showFormattingTip && (
        <div className="notice notice--info notice--small">
          To add basic formatting and links, refer to the{' '}
          <Link href="/formatting" target="_blank" title="Guide to formatting text on Timetoast">
            formatting guide
          </Link>{' '}
          for full details.
        </div>
      )}
      {content}
    </div>
  )
}

export default RichMarkdownEditor
